// MA-15: React hook that wires the outbox drain to online transitions.
//
// Three triggers:
//  1. Immediate drain on mount — covers the "app starts online after an
//     offline session" case (one-time backfill of the existing local mirror).
//  2. registerOnlineCallback — fires on every subsequent verified-online
//     transition (offline→reconnect scenario).
//  3. MA-111: schedulePeriodicDrain — a bounded periodic re-drain + an
//     immediate re-drain on tab-foreground. Trigger 2 only fires on a real
//     browser online/offline DOM event; a Supabase-only blip that never
//     flips navigator.onLine (the device stays "online" throughout) was
//     otherwise never retried again for the rest of the tab's life.
//
// Guards (applied in useEffect deps + drainOutbox internally):
//  - persistEnabled (useRealSession) — no upload without an auth session.
//  - Both IDs present — localChildId + canonicalStudentId required.
//  - Circuit breaker + draining flag — inside drainOutbox.
//  - Per-child sync cursor (inside _drainAttempts) — bounds a periodic
//    tick's cost to the un-synced tail, not the whole local mirror.

import { useEffect } from "react";
import { useRealSession } from "@/lib/persistence";
import { registerOnlineCallback } from "@/lib/reachability";
import { drainOutbox, schedulePeriodicDrain } from "./outboxDrain";
import { consumeUndrainedEvictionCount as consumeAttemptEvictions } from "./localAttemptStore";
import { consumeUndrainedEvictionCount as consumeMilestoneEvictions } from "./localMilestoneStore";

/** Mount this hook wherever both the local child id and the canonical
 *  Supabase students.id are available. It registers a drain that fires
 *  on every verified-online transition, on a bounded periodic interval
 *  (+ tab-foreground), and once immediately on mount.
 *
 *  MA-376 (c): also checks, once per mount, whether a prior fully-offline
 *  stretch outgrew the local mirror's cap and had to evict an un-drained
 *  row (the write side counts this — see localAttemptStore/
 *  localMilestoneStore's trimToCap). `onUndrainedEviction` fires ONCE with
 *  the total count and the counter is cleared, matching the calm,
 *  once-per-occurrence posture of persistFailureToast — never a recurring
 *  alarm on every boot. */
export function useOutboxDrain(
  localChildId: string | null | undefined,
  canonicalStudentId: string | null | undefined,
  onUndrainedEviction?: (count: number) => void,
): void {
  const persistEnabled = useRealSession();

  useEffect(() => {
    if (!persistEnabled || !localChildId || !canonicalStudentId) return;

    const opts = { localChildId, canonicalStudentId };
    const drain = () => {
      void drainOutbox(opts);
    };

    if (onUndrainedEviction) {
      const evicted = consumeAttemptEvictions(localChildId) + consumeMilestoneEvictions(localChildId);
      if (evicted > 0) onUndrainedEviction(evicted);
    }

    // Fire immediately — handles app-startup backfill and the case where
    // IDs become available after the last online event fired.
    drain();

    // Register for future online transitions.
    const unregisterOnline = registerOnlineCallback(drain);
    // MA-111: bounded periodic + tab-foreground re-drain.
    const periodic = schedulePeriodicDrain(opts);

    return () => {
      unregisterOnline();
      periodic.stop();
    };
  }, [persistEnabled, localChildId, canonicalStudentId, onUndrainedEviction]);
}
